import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { connect } from "react-redux";
import { Popup } from "devextreme-react/popup";
import { Button } from "devextreme-react/button";
import {
    DataGrid,
    Column,
    Scrolling,
    Selection,
    Lookup,
} from "devextreme-react/data-grid";
import notify from "devextreme/ui/notify";
import axios from "axios";
import routes from "../../../../../assets/apiRoutes.json";
import { MESSAGES } from "../../../../../common/Messages";
import { CostComponentRateTypeOptions } from "../../../../../enum/Enum";

// uOMD0038 quantity fields: DisplayFormat ',0.00;,0.00-'.
const QUANTITY_FORMAT = { type: "fixedPoint", precision: 2, useGrouping: true };
// Prices and rates had no display format in Delphi, so keep up to four decimals rather than round them.
const PRICE_FORMAT = "#,##0.00##";
// qryOMTransactionDetailQuantityOrderedGetText: a zero quantity shows as blank.
const blankWhenZero = (cellInfo) => (cellInfo.value ? cellInfo.valueText : "");

const notifyError = (err, fallback) =>
    notify({ message: err?.response?.data?.message || fallback, width: 300 }, "error", 5000);

/**
 * Legacy uOMD0038 "Select Cost Components". The order's item lines are on top: the line the dialog was
 * opened for, or all of them after Show All. The selected line's Vendor CR cost components are on the
 * left (double-click to assign) and the ones it already has are on the right (double-click to remove).
 * Each change is saved and re-prices the line straight away, as Delphi's applied updates did, so
 * `onClosed(changed)` tells the caller whether the detail grid needs re-reading.
 */
function SelectCostComponentsPopup(props) {
    const { showPopup, setShowPopup, setLoadPanelVisible, onClosed } = props;
    const visible = showPopup.visible === true;
    const headerRowId = showPopup.headerRowId;
    const openedDetailRowId = showPopup.detailRowId;

    // ShowCostComponents opens on the current line only, with the button offering "Show All".
    const [showCurrentOnly, setShowCurrentOnly] = useState(true);
    const [parentRows, setParentRows] = useState([]);
    const [selectedDetailRowId, setSelectedDetailRowId] = useState(openedDetailRowId ?? null);
    const [vendorRows, setVendorRows] = useState([]);
    const [assignedRows, setAssignedRows] = useState([]);
    const changedRef = useRef(false);
    const closedRef = useRef(false);
    // Numbers each child-grid load so a slower, older answer cannot overwrite a newer one.
    const lineRequestRef = useRef(0);

    const accessToken = props.oidc?.user?.access_token;
    const apiBase = window._env_?.REACT_APP_APIS || "";

    const loadParentRows = useCallback(async () => {
        if (!visible || !headerRowId) return;
        setLoadPanelVisible(true);
        try {
            const res = await axios.get(`${apiBase}/${routes.OM.GetCostComponentsParentGrid}`, {
                params: { headerRowId, showCurrentOnly, detailRowId: openedDetailRowId },
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            const rows = Array.isArray(res.data) ? res.data : [];
            setParentRows(rows);
            // Stay on the selected line while it is listed; otherwise land on the first one, as the
            // reopened Delphi query did.
            setSelectedDetailRowId((current) =>
                rows.some((row) => row.RowId === current) ? current : rows[0]?.RowId ?? null);
        } catch (err) {
            setParentRows([]);
            notifyError(err, MESSAGES.COST_COMPONENTS_LOAD_FAILED);
        } finally {
            setLoadPanelVisible(false);
        }
    }, [visible, headerRowId, openedDetailRowId, showCurrentOnly, accessToken, apiBase, setLoadPanelVisible]);

    const loadSelectedLine = useCallback(async (detailRowId) => {
        const request = ++lineRequestRef.current;
        if (!detailRowId) {
            setVendorRows([]);
            setAssignedRows([]);
            return;
        }
        setLoadPanelVisible(true);
        try {
            const config = { params: { detailRowId }, headers: { Authorization: `Bearer ${accessToken}` } };
            const [vendorRes, assignedRes] = await Promise.all([
                axios.get(`${apiBase}/${routes.OM.GetVendorCRCostComponents}`, config),
                axios.get(`${apiBase}/${routes.OM.GetAssignedCostComponents}`, config),
            ]);
            if (request !== lineRequestRef.current) return;
            setVendorRows(Array.isArray(vendorRes.data) ? vendorRes.data : []);
            setAssignedRows(Array.isArray(assignedRes.data) ? assignedRes.data : []);
        } catch (err) {
            if (request !== lineRequestRef.current) return;
            setVendorRows([]);
            setAssignedRows([]);
            notifyError(err, MESSAGES.COST_COMPONENTS_LOAD_FAILED);
        } finally {
            setLoadPanelVisible(false);
        }
    }, [accessToken, apiBase, setLoadPanelVisible]);

    useEffect(() => {
        loadParentRows();
    }, [loadParentRows]);

    useEffect(() => {
        if (visible) loadSelectedLine(selectedDetailRowId);
    }, [visible, selectedDetailRowId, loadSelectedLine]);

    // An assignment or a removal re-prices the line, so its Amount is read again with the two lists.
    const saveChange = useCallback(async (route, params, failureMessage) => {
        setLoadPanelVisible(true);
        try {
            await axios.post(`${apiBase}/${route}`, null, {
                params,
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            changedRef.current = true;
        } catch (err) {
            notifyError(err, failureMessage);
        } finally {
            setLoadPanelVisible(false);
        }
        await Promise.all([loadParentRows(), loadSelectedLine(selectedDetailRowId)]);
    }, [accessToken, apiBase, selectedDetailRowId, loadParentRows, loadSelectedLine, setLoadPanelVisible]);

    // A fresh array only when the selection really changes; a new one on every render makes the grid
    // re-apply the selection and report it back, and the two then chase each other.
    const selectedRowKeys = useMemo(
        () => (selectedDetailRowId ? [selectedDetailRowId] : []),
        [selectedDetailRowId]
    );

    // The grid reports an empty selection while it re-renders its rows; the Delphi grid always had a
    // current record, so only an actual row choice moves the selection.
    const onParentSelectionChanged = (e) => {
        const rowId = e.selectedRowsData?.[0]?.RowId;
        if (rowId) setSelectedDetailRowId(rowId);
    };

    // grdTransLocationsDblClick
    const onVendorRowDblClick = (e) => {
        if (!e.data || !selectedDetailRowId) return;
        saveChange(
            routes.OM.AssignCostComponent,
            { detailRowId: selectedDetailRowId, vendorCRRowId: e.data.RowId },
            MESSAGES.COST_COMPONENT_ASSIGN_FAILED
        );
    };

    // grdAvlLocationsDblClick
    const onAssignedRowDblClick = (e) => {
        if (!e.data) return;
        saveChange(
            routes.OM.UnassignCostComponent,
            { detailRateRowId: e.data.RowId },
            MESSAGES.COST_COMPONENT_REMOVE_FAILED
        );
    };

    // grdTransLocationsCustomDraw: a rate whose Date To has passed is drawn in the warning colour.
    const onVendorCellPrepared = (e) => {
        if (e.rowType !== "data" || !e.data?.DateTo) return;
        if (new Date(e.data.DateTo) < new Date()) {
            e.cellElement.style.color = window._env_.NegativeValueColor;
        }
    };

    const close = () => {
        if (closedRef.current) return;
        closedRef.current = true;
        setShowPopup((prev) => ({ ...prev, visible: false }));
        if (onClosed) onClosed(changedRef.current);
    };

    if (!visible) return null;

    return (
        <Popup
            visible={true}
            onHiding={close}
            dragEnabled={false}
            hideOnOutsideClick={false}
            showCloseButton={true}
            showTitle={true}
            title={showPopup.title ?? MESSAGES.COST_COMPONENTS_TITLE}
            container="#root"
            width="90vw"
            height="90vh"
        >
            <div style={{ display: "flex", flexDirection: "column", height: "100%", gap: 8 }}>
                <div style={{ flex: "0 0 220px", minHeight: 200 }}>
                    <DataGrid
                        dataSource={parentRows}
                        keyExpr="RowId"
                        showBorders={true}
                        columnAutoWidth={true}
                        height="100%"
                        selectedRowKeys={selectedRowKeys}
                        onSelectionChanged={onParentSelectionChanged}
                    >
                        <Selection mode="single" />
                        <Scrolling mode="standard" />
                        <Column dataField="Warehouse" caption="WH" />
                        <Column dataField="Item" caption="Item" />
                        <Column dataField="ItemDescription" caption="Item Description" />
                        <Column dataField="Lot" caption="Lot" />
                        <Column dataField="LotDescription" caption="Lot Description" />
                        <Column dataField="Ordered" caption="Ordered" dataType="number" format={QUANTITY_FORMAT} customizeText={blankWhenZero} />
                        <Column dataField="Shipped" caption="Shipped" dataType="number" format={QUANTITY_FORMAT} customizeText={blankWhenZero} />
                        <Column dataField="BackOrder" caption="BackOrder" dataType="number" format={QUANTITY_FORMAT} customizeText={blankWhenZero} />
                        <Column dataField="UnitPrice" caption="Unit Price" dataType="number" format={PRICE_FORMAT} />
                        <Column dataField="Amount" caption="Amount" dataType="number" format={QUANTITY_FORMAT} />
                    </DataGrid>
                </div>
                <div style={{ flex: "1 1 200px", display: "flex", gap: 8, minHeight: 180 }}>
                    <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
                        <div style={{ fontWeight: "bold", marginBottom: 4 }}>Vendor CR Cost Components (doubleclick to assign)</div>
                        <DataGrid
                            dataSource={vendorRows}
                            keyExpr="RowId"
                            showBorders={true}
                            columnAutoWidth={true}
                            height="100%"
                            onRowDblClick={onVendorRowDblClick}
                            onCellPrepared={onVendorCellPrepared}
                        >
                            <Scrolling mode="standard" />
                            <Column dataField="Vendor" caption="Vendor" />
                            <Column dataField="ComponentCode" caption="Component Code" />
                            <Column dataField="Group" caption="Group" dataType="number" />
                            <Column dataField="DateFrom" caption="Date From" dataType="date" format="shortDate" />
                            <Column dataField="DateTo" caption="Date To" dataType="date" format="shortDate" />
                            <Column dataField="RateType" caption="Rate Type">
                                <Lookup dataSource={CostComponentRateTypeOptions} valueExpr="id" displayExpr="text" />
                            </Column>
                            <Column dataField="Rate" caption="Rate" dataType="number" format={PRICE_FORMAT} />
                        </DataGrid>
                    </div>
                    <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
                        <div style={{ fontWeight: "bold", marginBottom: 4 }}>Assigned Cost Components (doubleclick to remove)</div>
                        <DataGrid
                            dataSource={assignedRows}
                            keyExpr="RowId"
                            showBorders={true}
                            columnAutoWidth={true}
                            height="100%"
                            onRowDblClick={onAssignedRowDblClick}
                        >
                            <Scrolling mode="standard" />
                            <Column dataField="ComponentCode" caption="Component Code" />
                            <Column dataField="Group" caption="Group" dataType="number" />
                            <Column dataField="RateType" caption="Rate Type">
                                <Lookup dataSource={CostComponentRateTypeOptions} valueExpr="id" displayExpr="text" />
                            </Column>
                            <Column dataField="Rate" caption="Rate" dataType="number" format={PRICE_FORMAT} />
                            <Column dataField="Quantity" caption="Quantity" dataType="number" format={QUANTITY_FORMAT} />
                            <Column dataField="Amount" caption="Amount" dataType="number" format={QUANTITY_FORMAT} />
                        </DataGrid>
                    </div>
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, paddingTop: 8, borderTop: "1px solid #ddd" }}>
                    <Button text={showCurrentOnly ? "Show All" : "Show Current"} onClick={() => setShowCurrentOnly((prev) => !prev)} />
                    <Button text="Close" type="default" onClick={close} />
                </div>
            </div>
        </Popup>
    );
}

function mapStateToProps(state) {
    return { oidc: state.oidc };
}

export default connect(mapStateToProps)(SelectCostComponentsPopup);
